/* eslint-disable @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access */
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { forkJoin, Observable, of } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'

import { ClientStatus } from '@shared/models/client.model'
import { StatusHistoryDialogComponent } from '@app/modules/admin/shared/status-history-dialog/status-history-dialog.component'
import { User } from '@shared/models/user.model'
import { DataService } from '@shared/services/data.service'
import { resellerStatuses } from '@core/constants/reseller-statuses'
import { PermissionsService } from '@shared/services/permissions.service'
import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { PermissionType } from '@core/types/permission-type.type'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { DialogService } from '@shared/dialogs/dialog.service'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { RoleType } from '@core/types/role.type'
import { AuthService } from '@app/modules/auth/auth.service'
import { Timezone } from '@shared/models/timezone.model'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { PagesData } from '@shared/models/pages-data.model'
import { Reseller, ResellerUser } from '@shared/models/reseller.model'
import { NoteHistory } from '@shared/models/note.model'
import { History } from '@shared/models/history.model'
import { DestroyService } from '@core/services/destroy.service'
import { ResellerService } from '@app/modules/admin/reseller/services/reseller.service'
import { ResellerFormSave } from '@app/modules/admin/reseller/models/reseller-form-save.model'

@Component({
  selector: 'lin-reseller-form',
  templateUrl: './reseller-form.component.html',
  styles: [':host { width: 100%; height: 100%; }'],
  providers: [DestroyService],
  standalone: false,
})
export class ResellerFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  form: FormGroup
  formError: string
  isSubmitting = false
  resellerId: string | number
  timezones: Timezone[]
  isLoaded = false
  adminNotesHistory: NoteHistory[]
  resellerNotesHistory: NoteHistory[]
  resellerUsers: ResellerUser[]
  salesRepresentatives: User[]
  accountManagers: User[]
  statuses = resellerStatuses
  formStatuses = resellerStatuses
  PermissionType = PermissionType
  isFormChanged: ReturnType<typeof takeFormSnapshot>
  reseller: Reseller
  readonly required = new Set<string>()

  get user(): User {
    return this.authService.getCurrentUser()
  }

  get showChevronAccountId(): boolean {
    return this.user.team?.options?.isChevron || (this.user.isAdmin() && this.reseller?.team?.options?.isChevron)
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly dialog: DialogService,
    private readonly dataService: DataService,
    private readonly adminTeamService: AdminTeamService,
    private readonly permissionsService: PermissionsService,
    private readonly authService: AuthService,
    private readonly unsavedFormTrackerService: UnsavedFormTracker,
    private readonly cdr: ChangeDetectorRef,
    private readonly resellerService: ResellerService,
    private readonly destroyService: DestroyService,
  ) {}

  ngOnInit(): void {
    this.unsavedFormTrackerService.track(this)
    this.resellerId = this.route.parent.snapshot.params.id || this.user.team.resellerId
    this.createForm()

    const team$ = this.fetchFormData()
    const timezones$ = this.dataService.getTimezones()

    forkJoin([timezones$, team$])
      .pipe(takeUntil(this.destroyService))
      .subscribe(([timezones]) => {
        this.timezones = timezones
        this.isLoaded = true

        this.cdr.markForCheck()
      })

    this.getNotes()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fetchFormData(): Observable<Reseller | Partial<ResellerUser> | any> {
    if (this.resellerId) {
      return this.fetchResellerUsers()
    }
    return this.fetchResellerManagers()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fetchResellerUsers(): Observable<Reseller | Partial<ResellerUser> | any> {
    const action$ = this.user.isAdmin() ? this.fetchResellerManagers() : of(null)
    const isTeamMember = ({ team }: ResellerUser) => team?.id === this.reseller?.team?.id

    return action$.pipe(
      switchMap(() => this.resellerService.getResellerById(this.resellerId)),
      tap((reseller) => (this.reseller = reseller)),
      switchMap(() =>
        this.resellerService.getResellerUsers(this.resellerId, 0, 1000, null, { role: RoleType.ResellerAdmin }, [
          'id',
          'name',
          'surname',
          'email',
          'team',
        ]),
      ),
      map((users) => users.data),
      map((users) => users.filter(isTeamMember)),
      tap((users) => {
        this.resellerUsers = users
        this.setFormValues()
      }),
    )
  }

  private fetchResellerManagers(): Observable<[PagesData<User>, PagesData<User>]> {
    return forkJoin([
      this.adminTeamService.getAdminTeam(0, 1000, null, { role: RoleType.SalesRepresentative }, ['id', 'name', 'surname', 'status']).pipe(
        tap((salesRepresentatives) => {
          const isNotBlocked = ({ status }) => status !== ClientStatus.Blocked
          this.salesRepresentatives = salesRepresentatives.data.filter(isNotBlocked)
        }),
      ),
      this.adminTeamService.getAdminTeam(0, 1000, null, { role: RoleType.AccountManager }, ['id', 'name', 'surname', 'status']).pipe(
        tap((accountManagers) => {
          const isNotBlocked = ({ status }) => status !== ClientStatus.Blocked
          this.accountManagers = accountManagers.data.filter(isNotBlocked)
        }),
      ),
    ])
  }

  hasPermission(permissionType: PermissionType): boolean {
    return this.permissionsService.hasPermissionSync(permissionType)
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      name: '',
      legalName: '',
      taxNr: ['', [Validators.required, Validators.minLength(11)]],
      chevronAccountId: '',
      legalAddress: '',
      billingAddress: '',
      status: '',
      keyContact: '',
      timezone: '',
      salesRepresentative: '',
      accountManager: '',
      resellerNote: '',
      adminNote: '',
      language: '',
    })

    this.required.add('taxNr')
    const requiredFields = ['name', 'status']
    requiredFields.forEach((fieldName) => {
      this.setRequired(fieldName)
    })

    if (this.user.isReseller()) {
      this.form.get('salesRepresentative').disable()
      this.form.get('accountManager').disable()
      this.form.get('status').disable()
    }
    !this.hasPermission(PermissionType.ResellerEdit) && this.form.disable()

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  getStatusHistory(): void {
    this.isLoaded = false

    this.resellerService
      .getResellerStatusHistory(this.resellerId)
      .pipe(filter(Boolean), takeUntil(this.destroyService))
      .subscribe((history: History[]): void => {
        this.isLoaded = true

        this.cdr.markForCheck()
        this.openStatusHistoryDialog(history)
      })
  }

  openStatusHistoryDialog(history: History[]): void {
    this.dialog.open(StatusHistoryDialogComponent, this.translateService.instant('ADMIN.DEVICES.STATUS_HISTORY') as string, { history })
  }

  saveData(): void {
    this.isSubmitting = true
    const saveData = this.prepareDataForSave()
    const isCreate = !this.resellerId
    const action$ = this.resellerId
      ? this.resellerService.updateReseller(this.resellerId, saveData)
      : this.resellerService.addReseller(saveData)

    action$
      .pipe(
        switchMap((reseller) =>
          isCreate
            ? of(reseller)
            : this.resellerService.getResellerById(this.resellerId).pipe(
                tap((fresh) => (this.reseller = fresh)),
                map(() => reseller),
              ),
        ),
        finalize(() => (this.isSubmitting = false)),
        takeUntil(this.destroyService),
      )
      .subscribe((reseller) => {
        if (isCreate) {
          // CREATE branch redirects to the new-user screen -> reload+reset does not apply.
          this.isFormChanged = takeFormSnapshot(this.form)
          this.form.markAsPristine()
          this.cdr.markForCheck()
          void this.router.navigate(['admin', 'resellers', reseller.id, 'users', 'new'])
          return
        }
        // EDIT branch stays on page: repopulate from fresh entity, re-baseline, mark pristine.
        this.setFormValues()
        this.form.markAsPristine()
        this.cdr.markForCheck()
      }, handleFormErrors(this.form))
  }

  sendEmail(event: Event, fieldName: string): void {
    event.stopPropagation()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.form.value[fieldName]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const email = this.resellerUsers.find((u) => u.id === this.form.value[fieldName]).email
      location.href = `mailto:${email}`
    }
  }

  getNotes() {
    if (!this.resellerId) return

    const isAdminWithPermission = this.user?.isAdmin() && this.hasPermission(PermissionType.ResellerNotesHistory)
    const resellerNotesHistory$ = this.resellerService.getResellerNotes(this.resellerId, 'reseller')
    const adminNotesHistory$ = isAdminWithPermission ? this.resellerService.getResellerNotes(this.resellerId, 'admin') : of(null)

    forkJoin([resellerNotesHistory$, adminNotesHistory$])
      .pipe(takeUntil(this.destroyService))
      .subscribe(([resellerNotes, adminNotes]) => {
        this.adminNotesHistory = adminNotes
        this.resellerNotesHistory = resellerNotes
        this.cdr.markForCheck()
      })
  }

  addNote(noteType: 'admin' | 'reseller', noteValue: string): void {
    this.resellerService.addResellertNote(this.resellerId, noteValue, noteType).subscribe(() => {
      this.getNotes()
    })
  }

  deleteNote(id: number): void {
    this.resellerService
      .deleteResellertNote(id)
      .pipe(takeUntil(this.destroyService))
      .subscribe(() => {
        this.getNotes()
      })
  }

  private setFormValues(): void {
    const { reseller } = this
    const salesRepresentativeValue = reseller.salesManager
      ? this.user.isReseller()
        ? reseller.salesFullName
        : reseller.salesManager.id
      : null
    const accountManagerValue = reseller.manager ? (this.user.isReseller() ? reseller.managerFullName : reseller.manager.id) : null

    this.form.setValue({
      name: reseller.companyName,
      legalName: reseller.legalName,
      taxNr: reseller.taxNr,
      chevronAccountId: reseller.chevronAccountId || '',
      legalAddress: reseller.legalAddress,
      billingAddress: reseller.billingAddress,
      status: reseller.status,
      keyContact: reseller.keyContact?.id || '',
      timezone: reseller.timezone?.id || '',
      salesRepresentative: salesRepresentativeValue,
      accountManager: accountManagerValue,
      resellerNote: '',
      adminNote: '',
      language: reseller.language,
    })

    this.showChevronAccountId && this.setRequired('chevronAccountId')

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private prepareDataForSave(): ResellerFormSave {
    const formData = this.form.value
    return {
      companyName: formData.name,
      legalName: formData.legalName,
      taxNr: formData.taxNr,
      ...(this.showChevronAccountId && { chevronAccountId: formData.chevronAccountId }),
      legalAddress: formData.legalAddress,
      billingAddress: formData.billingAddress,
      status: formData.status,
      timezone: formData.timezone,
      managerId: formData.accountManager,
      salesManagerId: formData.salesRepresentative,
      resellerNote: formData.resellerNote,
      adminNote: formData.adminNote,
      keyContactId: formData.keyContact,
      language: formData.language,
    }
  }

  private setRequired(field: string): void {
    this.form.get(field).setValidators([Validators.required])
    this.form.get(field).updateValueAndValidity()
    this.required.add(field)
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
  }
}
